import { useEffect, useState } from "react";
import { X } from "lucide-react";

const STORAGE_KEY = "alternavit_consent_v1";

export function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!readConsent()) setVisible(true);
  }, []);

  const save = (choice) => {
    const record = {
      necessary: true,
      analytics: choice === "all" ? true : choice === "reject" ? false : analytics,
      marketing: choice === "all" ? true : choice === "reject" ? false : marketing,
      accepted_at: new Date().toISOString(),
      version: 1,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="no-print fixed bottom-0 left-0 right-0 z-50 px-4 sm:px-6 pb-4 sm:pb-6 fade-up"
      data-testid="cookie-banner"
    >
      <div
        className="max-w-3xl mx-auto card-soft p-5 sm:p-6 shadow-lg"
        style={{ background: "var(--bg)", boxShadow: "0 20px 60px rgba(42,51,43,0.18)" }}
      >
        <div className="flex justify-between items-start gap-3 mb-3">
          <div>
            <p className="label-eyebrow mb-1">A note on privacy</p>
            <h3 className="font-serif text-xl leading-snug">We respect your data</h3>
          </div>
          <button
            onClick={() => save("reject")}
            aria-label="Close"
            className="p-1 rounded-full hover:bg-[var(--bg-2)]"
            data-testid="cookie-close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--text-2)" }}>
          We use a small amount of strictly-necessary storage to keep you signed in. Optionally, you can let us
          collect anonymous analytics or measure marketing campaigns. You can change your choice anytime in{" "}
          <a href="/privacy" className="underline" style={{ color: "var(--brand)" }}>
            Privacy
          </a>
          .
        </p>

        {showDetail && (
          <div className="space-y-2 mb-4" data-testid="cookie-detail">
            <Row label="Strictly necessary" desc="Required for sign-in and core features. Always on." disabled checked />
            <Row
              label="Analytics"
              desc="Anonymous, aggregated usage data so we can improve the product."
              checked={analytics}
              onChange={setAnalytics}
              testid="cookie-analytics"
            />
            <Row
              label="Marketing"
              desc="Helps us measure which campaigns lead to sign-ups. Off by default."
              checked={marketing}
              onChange={setMarketing}
              testid="cookie-marketing"
            />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button className="btn-primary text-sm" onClick={() => save("all")} data-testid="cookie-accept-all">
            Accept all
          </button>
          <button className="btn-ghost text-sm" onClick={() => save("reject")} data-testid="cookie-reject">
            Reject optional
          </button>
          {showDetail ? (
            <button className="btn-ghost text-sm" onClick={() => save("custom")} data-testid="cookie-save">
              Save my choices
            </button>
          ) : (
            <button
              className="btn-ghost text-sm"
              onClick={() => setShowDetail(true)}
              data-testid="cookie-customise"
            >
              Customise
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, desc, checked, onChange, disabled, testid }) {
  return (
    <div
      className="flex items-start justify-between gap-3 p-3 rounded-xl"
      style={{ background: "var(--bg-2)" }}
    >
      <div>
        <p className="text-sm font-medium" style={{ color: "var(--text)" }}>{label}</p>
        <p className="text-xs mt-0.5" style={{ color: "var(--text-2)" }}>{desc}</p>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange && onChange(!checked)}
        className="shrink-0 w-10 h-6 rounded-full relative transition-colors"
        style={{
          background: checked ? "var(--brand)" : "var(--border)",
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        data-testid={testid}
      >
        <span
          className="absolute top-1 w-4 h-4 rounded-full bg-white transition-all"
          style={{ left: checked ? "calc(100% - 1.25rem)" : "0.25rem" }}
        />
      </button>
    </div>
  );
}
